import { Instagram, Loader2 } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { usePermissions } from '@/hooks/usePermissions'
import { useClienteSinPromoMails, useGuardarClienteSinPromoMails } from '@/hooks/useInstagram'

/**
 * Opt-out del bloque «Seguinos en Instagram» en los mails de este cliente (Ley 25.326, art. 27: el
 * titular puede pedir que no le manden publicidad). El bloque mismo le dice al cliente cómo pedirlo.
 *
 * Es un atributo del cliente, así que se gobierna con `editarCliente`: sin ese permiso se ve el estado
 * y nada más. Solo tiene efecto si el estudio activó el bloque en Configuración › Redes.
 */
export function PromoInstagramClienteCard({ clienteId }: { clienteId: string }) {
  const { puede } = usePermissions()
  const { data: sinPromo, isLoading, isError } = useClienteSinPromoMails(clienteId)
  const guardar = useGuardarClienteSinPromoMails()
  const id = `promo-instagram-cliente-${clienteId}`

  return (
    <div>
      <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
        <Instagram className="h-4 w-4" />
        Instagram en sus mails
      </h3>
      {isError ? (
        <p className="text-sm text-muted-foreground">No se pudo cargar esta preferencia.</p>
      ) : isLoading || sinPromo === undefined ? (
        // Sin dato todavía no se muestra el interruptor: prendido por defecto mentiría sobre un cliente con baja.
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Cargando preferencia" />
      ) : (
        <>
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor={id} className="text-sm font-normal">
              Mostrar el bloque «Seguinos en Instagram» en los mails a este cliente
            </Label>
            <Switch
              id={id}
              checked={!sinPromo}
              onCheckedChange={(mostrar) => guardar.mutate({ clienteId, sinPromo: !mostrar })}
              disabled={!puede.editarCliente || guardar.isPending}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Apagalo si el cliente pidió no verlo. Solo aplica si el estudio lo activó en Configuración › Redes.
          </p>
        </>
      )}
    </div>
  )
}
